package com.islamicmedia.shared.data.api

import com.islamicmedia.shared.domain.model.BulkResultItem
import com.islamicmedia.shared.domain.model.BulkResultStatus
import com.islamicmedia.shared.domain.model.BulkScanItem
import com.islamicmedia.shared.domain.model.BulkVerdict
import com.islamicmedia.shared.domain.model.SearchCriteria
import io.ktor.http.HttpMethod
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.put

/** Why a batch couldn't be started. */
sealed interface StartBatchOutcome {
    data class Started(val batchId: String) : StartBatchOutcome
    /** The pack can't cover every ticked book: [remaining] searches left, [needed] books ticked. */
    data class NotEnoughSearches(val remaining: Int, val needed: Int) : StartBatchOutcome
    data object NotLibrary : StartBatchOutcome
    data class Failed(val message: String) : StartBatchOutcome
}

/** What a progress check found. */
sealed interface BatchProgress {
    data class Update(val items: List<BulkResultItem>, val done: Boolean) : BatchProgress
    /** The server doesn't know this batch (or it isn't ours). */
    data object NotFound : BatchProgress
    /** Couldn't reach the server this time; the batch keeps running there, so just ask again. */
    data object Unreachable : BatchProgress
}

/** Starts a server-side batch analysis of the ticked books and reports its progress. */
class ScanBatchClient(private val api: AnthropicApiClient) {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    suspend fun start(items: List<BulkScanItem>, criteria: SearchCriteria): StartBatchOutcome {
        val body = buildJsonObject {
            put("items", buildJsonArray {
                items.forEach { i ->
                    add(buildJsonObject {
                        put("title", i.title)
                        if (i.author.isNotBlank()) put("author", i.author)
                        i.year?.takeIf { it.isNotBlank() }?.let { put("year", it) }
                    })
                }
            })
            put("criteria", buildJsonObject {
                put("customConcerns", criteria.customConcerns)
                put("childAgeRanges", buildJsonArray { criteria.childAgeRanges.forEach { add(JsonPrimitive(it)) } })
            })
        }.toString()
        val (status, text) = api.authedCall(HttpMethod.Post, "/api/scan-batch", body)
            ?: return StartBatchOutcome.Failed("Couldn't reach the server. Check your connection and try again.")
        val obj = runCatching { json.parseToJsonElement(text) as? JsonObject }.getOrNull()
        return when {
            status in 200..299 ->
                obj?.text("batchId")?.let { StartBatchOutcome.Started(it) }
                    ?: StartBatchOutcome.Failed("Something went wrong starting the analysis. Please try again.")
            status == 402 && obj?.text("error") == "not_enough_searches" -> StartBatchOutcome.NotEnoughSearches(
                remaining = obj.text("remaining")?.toIntOrNull() ?: 0,
                needed = obj.text("needed")?.toIntOrNull() ?: items.size
            )
            status == 403 -> StartBatchOutcome.NotLibrary
            status == 401 -> StartBatchOutcome.Failed("Please sign in again.")
            status == 429 -> StartBatchOutcome.Failed("You've started a lot of batches in the last hour. Please try again a little later.")
            else -> StartBatchOutcome.Failed("Something went wrong starting the analysis. Please try again.")
        }
    }

    suspend fun progress(batchId: String): BatchProgress {
        val (status, text) = api.authedCall(HttpMethod.Get, "/api/scan-batch/$batchId") ?: return BatchProgress.Unreachable
        if (status == 404) return BatchProgress.NotFound
        if (status !in 200..299) return BatchProgress.Unreachable
        return try {
            val obj = json.parseToJsonElement(text).jsonObject
            val items = (obj["items"] as? JsonArray).orEmpty().mapNotNull { (it as? JsonObject)?.toResult() }
            BatchProgress.Update(items, done = obj.text("done") == "true")
        } catch (e: Exception) {
            BatchProgress.Unreachable
        }
    }

    private fun JsonObject.toResult(): BulkResultItem? {
        val id = text("id") ?: return null
        return BulkResultItem(
            id = id,
            title = text("title") ?: return null,
            author = text("author").orEmpty(),
            year = text("year"),
            coverUrl = text("coverUrl"),
            status = when (text("status")) {
                "running" -> BulkResultStatus.RUNNING
                "done" -> BulkResultStatus.DONE
                "failed" -> BulkResultStatus.FAILED
                "refused" -> BulkResultStatus.REFUSED
                else -> BulkResultStatus.QUEUED
            },
            verdict = when (text("verdict")) {
                "minor" -> BulkVerdict.MINOR
                "moderate" -> BulkVerdict.MODERATE
                "major" -> BulkVerdict.MAJOR
                else -> null
            },
            error = text("error"),
            position = text("position")?.toIntOrNull() ?: 0,
            updatedAt = text("updatedAt")
        )
    }

    private fun JsonObject.text(key: String): String? =
        (this[key] as? JsonPrimitive)?.takeIf { it !is JsonNull }?.contentOrNull?.takeIf { it.isNotBlank() }
}
